/**
 * SegmentDriverList.jsx — Standardized presentation for segment-level
 * deviations from a baseline.
 *
 * Replaces ad-hoc "Top Drivers of Support / Top Drivers of Resistance",
 * "Friction Map" tables, and Compare delta lists with a single visual pattern.
 *
 * Each item:
 *   - segment name (the value, e.g. "Engineering")
 *   - dimension (e.g. "Department")
 *   - delta vs baseline (signed, in pp)
 *   - n (sample size)
 *   - risk/support badge
 *
 * Variants:
 *   - mode="table"  — dense, ranked table (FrictionMap style)
 *   - mode="cards"  — compact stacked rows (drivers-of-X style)
 *
 * Privacy:
 *   - Items with `masked: true` are rendered as "Masked (n<30)" with no metric.
 */
import { Badge } from '../ui/badge';
import { ArrowDown, ArrowUp, EyeOff, Info } from 'lucide-react';
import { cn } from '../../lib/utils';

const RISK_TONE = {
  severe: { bg: 'bg-negative-soft ', text: 'text-negative-soft-foreground ', border: 'border-negative-border ', label: 'Severe' },
  high: { bg: 'bg-negative-soft ', text: 'text-negative-soft-foreground ', border: 'border-negative-border ', label: 'High' },
  medium: { bg: 'bg-warning-soft ', text: 'text-warning-soft-foreground ', border: 'border-warning-border ', label: 'Medium' },
  low: { bg: 'bg-slate-50 dark:bg-slate-800', text: 'text-slate-500 dark:text-slate-400', border: 'border-slate-200 dark:border-border', label: 'Low' },
  positive: { bg: 'bg-positive-soft ', text: 'text-positive-soft-foreground ', border: 'border-positive-border ', label: 'Driver' },
};

function classifyRiskFromDelta(delta) {
  const a = Math.abs(delta || 0);
  if (delta > 5) return 'positive';
  if (a >= 15) return 'severe';
  if (a >= 10) return 'high';
  if (a >= 5) return 'medium';
  return 'low';
}

function normalizeItems(items) {
  return (items || []).map((it) => {
    const delta = it.delta ?? it.deviation_pp ?? 0;
    return {
      segment: it.segment ?? it.value ?? it.name ?? '—',
      dimension: it.dimension || '',
      delta,
      n: it.size ?? it.n ?? null,
      risk: it.risk || classifyRiskFromDelta(delta),
      masked: !!it.masked,
    };
  });
}

export default function SegmentDriverList({
  items,
  mode = 'table',
  emptyMessage = 'No significant segment deviations detected.',
  emptyHint = null,
  className,
  testId = 'segment-driver-list',
  showDimensionColumn = true,
}) {
  const normalized = normalizeItems(items);

  if (normalized.length === 0) {
    return (
      <div
        className={cn(
          'p-5 bg-slate-50 dark:bg-slate-800/30 rounded-lg border border-slate-100 dark:border-slate-700',
          className
        )}
        data-testid={`${testId}-empty`}
      >
        <div className="flex items-start gap-3">
          <Info className="w-5 h-5 text-slate-400 mt-0.5 flex-shrink-0" />
          <div>
            <p className="text-sm font-medium text-slate-700 dark:text-slate-200 mb-1">{emptyMessage}</p>
            {emptyHint && (
              <p className="text-xs text-slate-500 dark:text-slate-400 leading-relaxed">{emptyHint}</p>
            )}
          </div>
        </div>
      </div>
    );
  }

  if (mode === 'cards') {
    return (
      <div className={cn('space-y-2', className)} data-testid={testId}>
        {normalized.map((it, i) => (
          <SegmentCard key={i} index={i} item={it} testId={`${testId}-item-${i}`} />
        ))}
      </div>
    );
  }

  // mode === 'table'
  return (
    <div
      className={cn('border border-slate-200 dark:border-border rounded-lg overflow-hidden', className)}
      data-testid={testId}
    >
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-slate-50 dark:bg-muted border-b border-slate-200 dark:border-border">
            <th className="text-left px-4 py-2.5 text-[11px] uppercase tracking-wider text-slate-500 dark:text-slate-400 font-semibold">
              Segment
            </th>
            {showDimensionColumn && (
              <th className="text-left px-4 py-2.5 text-[11px] uppercase tracking-wider text-slate-500 dark:text-slate-400 font-semibold hidden sm:table-cell">
                Dimension
              </th>
            )}
            <th className="text-right px-4 py-2.5 text-[11px] uppercase tracking-wider text-slate-500 dark:text-slate-400 font-semibold">
              Delta vs Baseline
            </th>
            <th className="text-right px-4 py-2.5 text-[11px] uppercase tracking-wider text-slate-500 dark:text-slate-400 font-semibold hidden md:table-cell">
              n
            </th>
            <th className="text-right px-4 py-2.5 text-[11px] uppercase tracking-wider text-slate-500 dark:text-slate-400 font-semibold">
              Level
            </th>
          </tr>
        </thead>
        <tbody>
          {normalized.map((it, i) => (
            <SegmentRow
              key={i}
              item={it}
              showDimensionColumn={showDimensionColumn}
              testId={`${testId}-row-${i}`}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function SegmentRow({ item, showDimensionColumn, testId }) {
  const risk = RISK_TONE[item.risk] || RISK_TONE.low;
  const deltaCls =
    item.delta > 0
      ? 'text-positive-soft-foreground'
      : item.delta < -5
      ? 'text-negative-soft-foreground'
      : 'text-warning-soft-foreground';

  return (
    <tr
      className={cn(
        'border-b border-slate-50 last:border-0',
        item.risk === 'severe' && 'bg-negative-soft/40',
        item.risk === 'high' && 'bg-negative-soft/20'
      )}
      data-testid={testId}
    >
      <td className="px-4 py-2.5 font-medium text-slate-800 dark:text-slate-100">{item.segment}</td>
      {showDimensionColumn && (
        <td className="px-4 py-2.5 text-slate-400 hidden sm:table-cell">
          {item.dimension ? item.dimension.replace(/_/g, ' ') : '—'}
        </td>
      )}
      <td className="px-4 py-2.5 text-right font-mono font-semibold tabular-nums">
        {item.masked ? (
          <span className="inline-flex items-center gap-1 text-slate-400">
            <EyeOff className="w-3 h-3" /> Masked
          </span>
        ) : (
          <span className={deltaCls}>
            {item.delta > 0 ? '+' : ''}
            {item.delta.toFixed(1)}
          </span>
        )}
      </td>
      <td className="px-4 py-2.5 text-right text-slate-400 tabular-nums hidden md:table-cell">
        {item.n != null ? item.n : '—'}
      </td>
      <td className="px-4 py-2.5 text-right">
        {item.masked ? (
          <Badge variant="outline" className="text-[10px] text-slate-400 border-slate-200">
            n &lt; 30
          </Badge>
        ) : (
          <Badge variant="outline" className={cn('text-[10px] font-semibold', risk.bg, risk.text, risk.border)}>
            {risk.label}
          </Badge>
        )}
      </td>
    </tr>
  );
}

function SegmentCard({ item, index, testId }) {
  const isPositive = item.delta > 0;
  const Arrow = isPositive ? ArrowUp : ArrowDown;
  const arrowCls = isPositive ? 'text-positive-soft-foreground' : 'text-negative-soft-foreground';

  return (
    <div
      className="flex items-center gap-3 py-2 px-3 rounded-lg bg-slate-50 dark:bg-slate-800/30 border border-slate-100 dark:border-slate-700"
      data-testid={testId}
    >
      <span className="text-[10px] font-bold text-slate-400 w-4 text-center">{index + 1}</span>
      <Arrow className={cn('w-3.5 h-3.5 flex-shrink-0', arrowCls)} />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-slate-700 dark:text-slate-200 truncate">{item.segment}</p>
        {item.dimension && (
          <p className="text-[10px] text-slate-400">{item.dimension.replace(/_/g, ' ')}</p>
        )}
      </div>
      {item.n != null && <span className="text-[10px] text-slate-400 tabular-nums">n={item.n}</span>}
      {item.masked ? (
        <Badge variant="outline" className="text-[10px] text-slate-400 border-slate-200">
          Masked
        </Badge>
      ) : (
        <Badge
          variant="outline"
          className={cn(
            'text-[10px] font-mono font-semibold tabular-nums shrink-0',
            isPositive
              ? 'text-positive-soft-foreground bg-positive-soft border-positive-border'
              : 'text-negative-soft-foreground bg-negative-soft border-negative-border'
          )}
        >
          {item.delta > 0 ? '+' : ''}
          {item.delta.toFixed(1)}pp
        </Badge>
      )}
    </div>
  );
}
